// Ejemplos básicos.
import { at, part, wires, UNO } from './helpers.js';

const BB_TOP = { id: 'bb1', type: 'breadboard', x: 0, z: -80, rot: 0, props: { size: 'half' } };
// Protoboard a la derecha del Uno: llegan cables cortos de sus dos filas de pines.
const BB_SIDE = { id: 'bb1', type: 'breadboard', x: 115, z: 0, rot: 0, props: { size: 'half' } };

/** Agujero del riel (tp/tn arriba, bp/bn abajo) en la misma columna que `col` de la protoboard media. */
function rail(id, col) {
  const idx = col - 2;
  if (idx < 0 || idx % 6 === 5) throw new Error(`La columna ${col} no tiene agujero en el riel`);
  return `${id}${Math.floor(idx / 6) * 5 + (idx % 6) + 1}`;
}

const CAPS = 'Capacitores y 555';

export const basics = [
  {
    id: 'blink',
    category: 'Primeros pasos',
    name: 'Parpadeo (Blink)',
    description: 'El "hola mundo" de Arduino: el LED integrado (L, pin 13) se enciende y apaga cada medio segundo.',
    project: {
      name: 'Parpadeo',
      files: {
        'sketch.ino': `// Parpadeo: enciende y apaga el LED integrado de la placa (pin 13).

void setup() {
  pinMode(LED_BUILTIN, OUTPUT);   // el pin 13 será una salida
}

void loop() {
  digitalWrite(LED_BUILTIN, HIGH); // enciende el LED
  delay(500);                      // espera medio segundo
  digitalWrite(LED_BUILTIN, LOW);  // apaga el LED
  delay(500);
}
`,
      },
      parts: [UNO],
      wires: [],
    },
  },
  {
    id: 'blink-led',
    category: 'Primeros pasos',
    name: 'LED en protoboard',
    description: 'Un LED con su resistencia de 220 Ω en la protoboard, controlado por el pin 13.',
    project: {
      name: 'LED en protoboard',
      files: {
        'sketch.ino': `// Un LED externo con resistencia de 220 Ω en el pin 13.
// El cable verde va del pin 13 a la resistencia; la pata corta del LED (cátodo) va a GND.

const int LED = 13;

void setup() {
  pinMode(LED, OUTPUT);
}

void loop() {
  digitalWrite(LED, HIGH);
  delay(400);
  digitalWrite(LED, LOW);
  delay(400);
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('led1', 'led', { color: 'red' }, at(BB_TOP, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'h21', 'resistor')),
      ],
      wires: wires([
        ['uno:13', 'bb1:j25', 'green'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:bn11', 'uno:GND.1', 'black'],
      ]),
    },
  },
  {
    id: 'semaforo',
    category: 'Primeros pasos',
    name: 'Semáforo',
    description: 'Tres LEDs (rojo, amarillo y verde) que siguen la secuencia de un semáforo.',
    project: {
      name: 'Semáforo',
      files: {
        'sketch.ino': `// Semáforo con tres LEDs en los pines 10 (rojo), 9 (amarillo) y 8 (verde).

const int ROJO = 10;
const int AMARILLO = 9;
const int VERDE = 8;

void setup() {
  pinMode(ROJO, OUTPUT);
  pinMode(AMARILLO, OUTPUT);
  pinMode(VERDE, OUTPUT);
  Serial.begin(9600);
}

void luz(int pin, unsigned long ms, const char *nombre) {
  digitalWrite(ROJO, pin == ROJO);
  digitalWrite(AMARILLO, pin == AMARILLO);
  digitalWrite(VERDE, pin == VERDE);
  Serial.println(nombre);
  delay(ms);
}

void loop() {
  luz(VERDE, 3000, "Siga");
  for (int i = 0; i < 3; i++) {       // verde parpadeante
    digitalWrite(VERDE, LOW);
    delay(250);
    digitalWrite(VERDE, HIGH);
    delay(250);
  }
  luz(AMARILLO, 1000, "Precaución");
  luz(ROJO, 3000, "Alto");
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('led1', 'led', { color: 'red' }, at(BB_TOP, 'f8', 'led')),
        part('led2', 'led', { color: 'yellow' }, at(BB_TOP, 'f14', 'led')),
        part('led3', 'led', { color: 'green' }, at(BB_TOP, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'h9', 'resistor')),
        part('r2', 'resistor', { value: 220 }, at(BB_TOP, 'h15', 'resistor')),
        part('r3', 'resistor', { value: 220 }, at(BB_TOP, 'h21', 'resistor')),
      ],
      wires: wires([
        ['uno:10', 'bb1:j13', 'red'],
        ['uno:9', 'bb1:j19', 'yellow'],
        ['uno:8', 'bb1:j25', 'green'],
        ['bb1:j8', 'bb1:bn6', 'black'],
        ['bb1:j14', 'bb1:bn11', 'black'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:bn10', 'uno:GND.1', 'black'],
      ]),
    },
  },
  {
    id: 'fade',
    category: 'Primeros pasos',
    name: 'Desvanecer (PWM)',
    description: 'analogWrite() en el pin 9 sube y baja el brillo de un LED con modulación por ancho de pulso.',
    project: {
      name: 'Desvanecer',
      files: {
        'sketch.ino': `// Desvanecer: el brillo del LED sube y baja con PWM (analogWrite) en el pin 9.

const int LED = 9;      // pin con PWM (~)
int brillo = 0;
int paso = 5;

void setup() {
  pinMode(LED, OUTPUT);
}

void loop() {
  analogWrite(LED, brillo);   // 0 = apagado, 255 = máximo
  brillo += paso;
  if (brillo <= 0 || brillo >= 255) paso = -paso;
  delay(30);
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('led1', 'led', { color: 'blue' }, at(BB_TOP, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'h21', 'resistor')),
      ],
      wires: wires([
        ['uno:9', 'bb1:j25', 'blue'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:bn11', 'uno:GND.1', 'black'],
      ]),
    },
  },
  {
    id: 'boton',
    category: 'Entradas',
    name: 'Botón y LED',
    description: 'Un botón con INPUT_PULLUP enciende un LED. Mantenlo presionado con el mouse o con la tecla espacio.',
    project: {
      name: 'Botón y LED',
      files: {
        'sketch.ino': `// Botón en el pin 2 (con la resistencia pull-up interna) que enciende un LED en el pin 13.
// Con INPUT_PULLUP el pin lee HIGH suelto y LOW al presionar (el botón lo une a GND).

const int BOTON = 2;
const int LED = 13;
int anterior = HIGH;
int pulsaciones = 0;

void setup() {
  pinMode(BOTON, INPUT_PULLUP);
  pinMode(LED, OUTPUT);
  Serial.begin(9600);
  Serial.println("Presiona el botón (o la tecla espacio)");
}

void loop() {
  int estado = digitalRead(BOTON);
  digitalWrite(LED, estado == LOW ? HIGH : LOW);
  if (estado == LOW && anterior == HIGH) {
    pulsaciones++;
    Serial.print("Pulsaciones: ");
    Serial.println(pulsaciones);
    delay(20);   // antirrebote sencillo
  }
  anterior = estado;
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('btn1', 'pushbutton', { color: 'blue', key: ' ' }, at(BB_TOP, 'e10', 'pushbutton')),
        part('led1', 'led', { color: 'green' }, at(BB_TOP, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'h21', 'resistor')),
      ],
      wires: wires([
        ['uno:2', 'bb1:a10', 'yellow'],
        ['bb1:j12', 'bb1:bn10', 'black'],
        ['uno:13', 'bb1:j25', 'green'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:bn6', 'uno:GND.1', 'black'],
      ]),
    },
  },
  {
    id: 'potenciometro',
    category: 'Entradas',
    name: 'Potenciómetro y brillo',
    description: 'analogRead() en A0 lee la perilla y su valor ajusta el brillo de un LED. Gira la perilla con el mouse.',
    project: {
      name: 'Potenciómetro',
      files: {
        'sketch.ino': `// La perilla del potenciómetro (A0) controla el brillo del LED del pin 9.
// Gira la perilla arrastrando hacia arriba o abajo, o con la rueda del mouse.

const int POT = A0;
const int LED = 9;

void setup() {
  pinMode(LED, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  int lectura = analogRead(POT);              // 0 a 1023
  int brillo = map(lectura, 0, 1023, 0, 255); // 0 a 255
  analogWrite(LED, brillo);
  float voltaje = lectura * 5.0 / 1023.0;
  Serial.print("Lectura: ");
  Serial.print(lectura);
  Serial.print("  Voltaje: ");
  Serial.print(voltaje, 2);
  Serial.println(" V");
  delay(100);
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('led1', 'led', { color: 'yellow' }, at(BB_TOP, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'h21', 'resistor')),
        part('pot1', 'potentiometer', { value: 0.35 }, { x: 8, z: 50, rot: 180 }),
      ],
      wires: wires([
        ['uno:9', 'bb1:j25', 'blue'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:bn11', 'uno:GND.1', 'black'],
        ['pot1:VCC', 'uno:5V', 'red'],
        ['pot1:GND', 'uno:GND.2', 'black'],
        ['pot1:SIG', 'uno:A0', 'orange'],
      ]),
    },
  },
  {
    id: 'rgb',
    category: 'Luces',
    name: 'LED RGB',
    description: 'Mezcla de colores con un LED RGB de cátodo común en tres pines PWM.',
    project: {
      name: 'LED RGB',
      files: {
        'sketch.ino': `// LED RGB de cátodo común: cada color va a un pin PWM con su resistencia.

const int R = 11, G = 10, B = 9;

void color(int r, int g, int b) {
  analogWrite(R, r);
  analogWrite(G, g);
  analogWrite(B, b);
}

void setup() {
  pinMode(R, OUTPUT);
  pinMode(G, OUTPUT);
  pinMode(B, OUTPUT);
}

void loop() {
  // Recorre el círculo de colores
  for (int h = 0; h < 360; h += 2) {
    int x = (h % 120) * 255 / 120;
    if (h < 120)      color(255 - x, x, 0);
    else if (h < 240) color(0, 255 - x, x);
    else              color(x, 0, 255 - x);
    delay(15);
  }
  color(255, 255, 255);  // blanco
  delay(600);
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('rgb1', 'rgb-led', { common: 'cathode' }, at(BB_TOP, 'f12', 'rgb-led')),
        part('r1', 'resistor', { value: 220 }, at(BB_TOP, 'g12', 'resistor', { rot: 90, pin: '1' })),
        part('r2', 'resistor', { value: 220 }, at(BB_TOP, 'g14', 'resistor', { rot: 90, pin: '1' })),
        part('r3', 'resistor', { value: 220 }, at(BB_TOP, 'g15', 'resistor', { rot: 90, pin: '1' })),
      ],
      wires: wires([
        ['uno:11', 'bb1:a12', 'red'],
        ['uno:10', 'bb1:a14', 'green'],
        ['uno:9', 'bb1:a15', 'blue'],
        ['bb1:j13', 'bb1:bn11', 'black'],
        ['bb1:bn6', 'uno:GND.1', 'black'],
      ]),
    },
  },
  {
    id: 'melodia',
    category: 'Sonido',
    name: 'Melodía con zumbador',
    description: 'tone() toca el «Himno de la alegría» en un zumbador pasivo. ¡Sube el volumen!',
    project: {
      name: 'Melodía',
      files: {
        'sketch.ino': `// Himno de la alegría con tone() en un zumbador pasivo (pin 8).

const int ZUMBADOR = 8;

// Frecuencias de las notas (Hz)
const int DO = 262, RE = 294, MI = 330, FA = 349, SOL = 392;

int melodia[] = { MI, MI, FA, SOL, SOL, FA, MI, RE, DO, DO, RE, MI, MI, RE, RE,
                  MI, MI, FA, SOL, SOL, FA, MI, RE, DO, DO, RE, MI, RE, DO, DO };
int tiempos[] = { 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 3, 8, 2,
                  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 3, 8, 2 };

void setup() {
  Serial.begin(9600);
}

void loop() {
  for (int i = 0; i < 30; i++) {
    int ms = 1600 / tiempos[i];
    tone(ZUMBADOR, melodia[i], ms * 0.9);
    Serial.println(melodia[i]);
    delay(ms);
  }
  noTone(ZUMBADOR);
  delay(1500);
}
`,
      },
      parts: [
        UNO,
        BB_TOP,
        part('buzzer1', 'buzzer', { mode: 'passive' }, at(BB_TOP, 'f18', 'buzzer')),
      ],
      wires: wires([
        ['uno:8', 'bb1:j21', 'orange'],
        ['bb1:j18', 'bb1:bn15', 'black'],
        ['bb1:bn10', 'uno:GND.1', 'black'],
      ]),
    },
  },
  {
    id: 'nano-led',
    category: 'Placas',
    name: 'Arduino Nano en protoboard',
    description: 'El Nano montado sobre la protoboard hace parpadear un LED externo.',
    project: (() => {
      const bb = { id: 'bb1', type: 'breadboard', x: 0, z: 0, rot: 0, props: { size: 'half' } };
      return {
        name: 'Nano en protoboard',
        files: {
          'sketch.ino': `// Arduino Nano montado en la protoboard: parpadeo de un LED en el pin 13.

void setup() {
  pinMode(13, OUTPUT);
}

void loop() {
  digitalWrite(13, HIGH);
  delay(250);
  digitalWrite(13, LOW);
  delay(750);
}
`,
        },
        parts: [
          bb,
          part('nano', 'arduino-nano', {}, at(bb, 'e3', 'arduino-nano', { pin: '12' })),
          part('led1', 'led', { color: 'red' }, at(bb, 'f24', 'led')),
          part('r1', 'resistor', { value: 220 }, at(bb, 'h25', 'resistor')),
        ],
        wires: wires([
          ['bb1:j3', 'bb1:j29', 'green'],
          ['bb1:j24', 'bb1:bn20', 'black'],
          ['bb1:j16', 'bb1:bn13', 'black'],
        ]),
      };
    })(),
  },
  {
    id: 'mega-secuencia',
    category: 'Placas',
    name: 'Mega 2560: luces secuenciales',
    description: 'Ocho LEDs en los pines 22–29 de la doble fila del Mega encendiéndose de ida y vuelta.',
    project: (() => {
      const bb = { id: 'bb1', type: 'breadboard', x: 118, z: 0, rot: 0, props: { size: 'half' } };
      const parts = [{ id: 'mega', type: 'arduino-mega', x: 0, z: 0, rot: 0, props: {} }, bb];
      const list = [];
      const rail = [1, 4, 6, 9, 11, 14, 16, 19];
      const colors = ['red', 'orange', 'yellow', 'green', 'green', 'yellow', 'orange', 'red'];
      for (let i = 0; i < 8; i++) {
        const c = 2 + 3 * i;
        parts.push(part('led' + (i + 1), 'led', { color: colors[i] }, at(bb, 'f' + c, 'led')));
        parts.push(part('r' + (i + 1), 'resistor', { value: 220 }, at(bb, 'g' + (c + 1), 'resistor', { rot: 90, pin: '1' })));
        list.push(['mega:' + (22 + i), 'bb1:a' + (c + 1), 'green']);
        list.push(['bb1:j' + c, 'bb1:bn' + rail[i], 'black']);
      }
      list.push(['bb1:bn2', 'mega:GND.5', 'black']);
      return {
        name: 'Mega secuencial',
        files: {
          'sketch.ino': `// Luces secuenciales en los pines 22 a 29 del Arduino Mega.

const int PRIMERO = 22;
const int CANTIDAD = 8;

void setup() {
  for (int i = 0; i < CANTIDAD; i++) pinMode(PRIMERO + i, OUTPUT);
}

void enciende(int i) {
  for (int k = 0; k < CANTIDAD; k++) digitalWrite(PRIMERO + k, k == i);
  delay(80);
}

void loop() {
  for (int i = 0; i < CANTIDAD; i++) enciende(i);
  for (int i = CANTIDAD - 2; i > 0; i--) enciende(i);
}
`,
        },
        parts,
        wires: wires(list),
      };
    })(),
  },
  {
    id: 'capacitor-rc',
    category: CAPS,
    name: 'Carga y descarga de un capacitor',
    description: 'Un capacitor de 100 µF se carga y se descarga a través de 10 kΩ (τ = R·C = 1 s) y A0 dibuja la curva en la gráfica. El sketch mide τ.',
    project: {
      name: 'Carga y descarga RC',
      files: {
        'sketch.ino': `// Carga y descarga de un capacitor de 100 µF a través de una resistencia de 10 kΩ.
// La constante de tiempo es τ = R·C = 10 000 Ω × 0.0001 F = 1 s: en τ la carga llega al 63 %
// y en 5τ queda casi completa. El pin 7 carga 6 s y descarga otros 6; A0 mide el voltaje.
// Abre la gráfica para ver las curvas.

const int CARGA = 7;
const float TAU = 10000.0 * 100e-6;   // segundos

void setup() {
  Serial.begin(9600);
  pinMode(CARGA, OUTPUT);
  digitalWrite(CARGA, LOW);   // empieza descargado
  delay(500);
}

void fase(int nivel) {
  digitalWrite(CARGA, nivel);
  unsigned long t0 = millis();
  bool medido = false;
  while (millis() - t0 < 6000) {
    float v = analogRead(A0) * 5.0 / 1023.0;
    // τ es cuando la carga pasa el 63.2 % de 5 V (o la descarga baja al 36.8 %)
    if (!medido && (nivel == HIGH ? v >= 0.632 * 5.0 : v <= 0.368 * 5.0)) {
      medido = true;
      Serial.print(nivel == HIGH ? "Carga" : "Descarga");
      Serial.print(": τ medido = ");
      Serial.print((millis() - t0) / 1000.0, 2);
      Serial.print(" s (R·C = ");
      Serial.print(TAU, 2);
      Serial.println(" s)");
    }
    Serial.print("voltaje:");
    Serial.println(v, 3);
    delay(50);
  }
}

void loop() {
  fase(HIGH);
  fase(LOW);
}
`,
      },
      parts: [
        UNO,
        BB_SIDE,
        part('r1', 'resistor', { value: 10000 }, at(BB_SIDE, 'j5', 'resistor')),
        part('c1', 'capacitor-electrolytic', { value: '100', voltage: '25' }, at(BB_SIDE, 'g9', 'capacitor-electrolytic', { rot: 180, pin: '+' })),
      ],
      wires: wires([
        ['uno:7', 'bb1:f5', 'yellow'],
        ['uno:A0', 'bb1:f9', 'orange'],
        ['bb1:f10', 'bb1:' + rail('bn', 10), 'black'],
        ['uno:GND.2', 'bb1:bn1', 'black'],
      ]),
    },
  },
  {
    id: 'pwm-filtrado',
    category: CAPS,
    name: 'PWM filtrado con RC',
    description: 'analogWrite() en el pin 9 pasa por un filtro de 10 kΩ y 1 µF: A0 lee un voltaje casi constante con el rizo de los 490 Hz del PWM, que se ve en la gráfica.',
    project: {
      name: 'PWM filtrado',
      files: {
        'sketch.ino': `// PWM filtrado: el pin 9 da pulsos de 490 Hz y el filtro RC (10 kΩ y 1 µF, τ = 10 ms) los promedia.
// A0 lee un voltaje casi constante (ciclo × 5 V) con un rizo de ~0.25 V: cada ráfaga de lecturas
// rápidas lo muestra en la gráfica. Con un capacitor mayor el rizo baja, pero responde más lento.

const int PWM = 9;
const int CICLOS[] = { 64, 128, 192 };
const int N = 100;
int lecturas[N];

void setup() {
  Serial.begin(115200);
  pinMode(PWM, OUTPUT);
}

void loop() {
  for (int k = 0; k < 3; k++) {
    analogWrite(PWM, CICLOS[k]);
    delay(100);                                       // 10 τ: el filtro ya se asentó
    for (int i = 0; i < N; i++) lecturas[i] = analogRead(A0);   // ~112 µs cada una
    int lo = 1023, hi = 0;
    long suma = 0;
    for (int i = 0; i < N; i++) {
      Serial.print("A0:");
      Serial.println(lecturas[i] * 5.0 / 1023.0, 3);
      lo = min(lo, lecturas[i]);
      hi = max(hi, lecturas[i]);
      suma += lecturas[i];
    }
    Serial.print("analogWrite(");
    Serial.print(CICLOS[k]);
    Serial.print("): promedio ");
    Serial.print(suma * 5.0 / 1023.0 / N, 2);
    Serial.print(" V (esperado ");
    Serial.print(CICLOS[k] * 5.0 / 255.0, 2);
    Serial.print(" V), rizo ");
    Serial.print((hi - lo) * 5.0 / 1023.0, 2);
    Serial.println(" V");
  }
}
`,
      },
      parts: [
        UNO,
        BB_SIDE,
        part('r1', 'resistor', { value: 10000 }, at(BB_SIDE, 'j5', 'resistor')),
        part('c1', 'capacitor-ceramic', { value: '1u' }, at(BB_SIDE, 'g9', 'capacitor-ceramic')),
      ],
      wires: wires([
        ['uno:9', 'bb1:f5', 'blue'],
        ['uno:A0', 'bb1:f9', 'orange'],
        ['bb1:f10', 'bb1:' + rail('bn', 10), 'black'],
        ['uno:GND.2', 'bb1:bn1', 'black'],
      ]),
    },
  },
  {
    id: 'ne555-astable',
    category: CAPS,
    name: '555 astable: LED que parpadea solo',
    description: 'Un NE555 oscila con R1 = 10 kΩ, R2 = 47 kΩ y C = 10 µF (≈ 1.4 Hz) y enciende un LED; el Arduino mide la frecuencia con pulseIn() y la compara con la fórmula.',
    project: {
      name: '555 astable',
      files: {
        'sketch.ino': `// NE555 en modo astable: el capacitor de 10 µF se carga por R1 + R2 hasta 2/3 de VCC y se
// descarga por R2 hasta 1/3. La salida (pin 3 del 555) enciende el LED y llega al pin 2 del Arduino,
// que mide cuánto dura en alto y en bajo con pulseIn().
// Fórmulas de la hoja de datos: alto = 0.693·(R1 + R2)·C, bajo = 0.693·R2·C, f = 1.44 / ((R1 + 2·R2)·C).

const int SALIDA = 2;
const float R1 = 10000.0, R2 = 47000.0, C = 10e-6;

void setup() {
  Serial.begin(9600);
  pinMode(SALIDA, INPUT);
  Serial.print("Fórmula: f = ");
  Serial.print(1.44 / ((R1 + 2 * R2) * C), 2);
  Serial.println(" Hz");
}

void loop() {
  unsigned long alto = pulseIn(SALIDA, HIGH, 3000000UL);   // µs
  unsigned long bajo = pulseIn(SALIDA, LOW, 3000000UL);
  if (alto == 0 || bajo == 0) {
    Serial.println("El 555 no oscila: revisa las conexiones");
    return;
  }
  float periodo = (alto + bajo) / 1e6;
  Serial.print("alto: ");
  Serial.print(alto / 1000);
  Serial.print(" ms  bajo: ");
  Serial.print(bajo / 1000);
  Serial.print(" ms  f = ");
  Serial.print(1.0 / periodo, 2);
  Serial.print(" Hz  ciclo: ");
  Serial.print(100.0 * alto / (alto + bajo), 0);
  Serial.println(" %");
}
`,
      },
      parts: [
        UNO,
        BB_SIDE,
        part('u1', 'ne555', {}, at(BB_SIDE, 'f10', 'ne555')),
        part('r1', 'resistor', { value: 10000 }, at(BB_SIDE, rail('tp', 11), 'resistor', { rot: 90, pin: '2' })),
        part('r2', 'resistor', { value: 47000 }, at(BB_SIDE, 'd11', 'resistor')),
        part('c1', 'capacitor-electrolytic', { value: '10', voltage: '25' }, at(BB_SIDE, 'h10', 'capacitor-electrolytic')),
        part('c2', 'capacitor-ceramic', { value: '10n' }, at(BB_SIDE, 'b13', 'capacitor-ceramic')),
        part('r3', 'resistor', { value: 330 }, at(BB_SIDE, 'i12', 'resistor')),
        part('led1', 'led', { color: 'red' }, at(BB_SIDE, 'g16', 'led', { rot: 180, pin: 'A' })),
      ],
      wires: wires([
        ['uno:5V', 'bb1:bp1', 'red'],
        ['uno:GND.2', 'bb1:bn1', 'black'],
        ['bb1:' + rail('bp', 4), 'bb1:' + rail('tp', 4), 'red'],
        ['bb1:' + rail('bn', 5), 'bb1:' + rail('tn', 5), 'black'],
        ['bb1:a10', 'bb1:' + rail('tp', 10), 'red'],
        ['bb1:j10', 'bb1:' + rail('bn', 10), 'black'],
        ['bb1:j13', 'bb1:' + rail('bp', 14), 'red'],
        ['bb1:c15', 'bb1:c12', 'green'],
        ['bb1:a12', 'bb1:j11', 'green'],
        ['bb1:a14', 'bb1:' + rail('tn', 14), 'black'],
        ['bb1:j17', 'bb1:' + rail('bn', 17), 'black'],
        ['bb1:h12', 'uno:2', 'yellow'],
      ]),
    },
  },
  {
    id: 'sensor-capacitivo',
    category: CAPS,
    name: 'Sensor capacitivo con papel aluminio',
    description: 'La librería CapacitiveSensor mide cuánto tarda en cargarse la lámina a través de 1 MΩ. Haz clic sostenido sobre el papel aluminio: la lectura sube y se enciende el LED L.',
    project: {
      name: 'Sensor capacitivo',
      files: {
        'sketch.ino': `// Sensor capacitivo con la librería CapacitiveSensor (Paul Badger).
// El pin 4 (envío) carga la lámina a través de 1 MΩ y el pin 2 (recepción) cuenta cuánto tarda en
// cambiar de nivel. Al tocar el papel aluminio se suma la capacitancia del cuerpo y la cuenta crece.
// Haz clic sostenido sobre la lámina para tocarla.

#include <CapacitiveSensor.h>

CapacitiveSensor sensor(4, 2);   // envío, recepción (la lámina va en el de recepción)
const long UMBRAL = 1000;

void setup() {
  Serial.begin(9600);
  pinMode(LED_BUILTIN, OUTPUT);
}

void loop() {
  long valor = sensor.capacitiveSensor(30);   // 30 muestras; resta el valor sin tocar
  digitalWrite(LED_BUILTIN, valor > UMBRAL ? HIGH : LOW);
  Serial.println(valor);
  delay(50);
}
`,
      },
      parts: [
        UNO,
        BB_SIDE,
        part('r1', 'resistor', { value: 1000000 }, at(BB_SIDE, 'j10', 'resistor')),
        part('p1', 'touch-plate', {}, { x: 172, z: 56, rot: 0 }),
      ],
      wires: wires([
        ['uno:4', 'bb1:f10', 'yellow'],
        ['uno:2', 'bb1:f14', 'green'],
        ['p1:SIG', 'bb1:h14', 'orange'],
      ]),
    },
  },
];
